import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QuantityInput } from "@/components/ui/quantity-input";
import { Ban, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Product } from "@/types/product";

interface ProductQuantityControlsProps {
  product: Product;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  showCachedPricing?: boolean;
  disabled?: boolean;
  className?: string;
  warningClassName?: string;
}

export const ProductQuantityControls: React.FC<ProductQuantityControlsProps> = ({
  product,
  quantity,
  onQuantityChange,
  showCachedPricing = false,
  disabled = false,
  className,
  warningClassName,
}) => {
  const { t } = useTranslation();
  const availability = showCachedPricing ? product.currentAvailability : null;
  const isOutOfStock = availability === "out_of_stock";
  const isDiscontinued = availability === "discontinued";
  const isSpecialOrder = availability === "special_order";
  const isRequestable = isOutOfStock || isSpecialOrder;
  const isUnavailable = isDiscontinued;
  const isOverAvailableQuantity =
    product.currentQuantityAvailable != null &&
    quantity > product.currentQuantityAvailable;

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      if (!disabled) {
        onQuantityChange(newQuantity);
      }
    },
    [disabled, onQuantityChange],
  );

  const handleAddProduct = useCallback(() => {
    if (!disabled) {
      onQuantityChange(quantity + 1);
    }
  }, [disabled, onQuantityChange, quantity]);

  return (
    <>
      <div
        className={cn("flex items-center gap-2", className)}
        onClick={(e) => e.stopPropagation()}
      >
        {quantity > 0 ? (
          <>
            <QuantityInput
              value={quantity}
              onChange={handleQuantityChange}
              min={0}
              max={9999}
              disabled={disabled || isUnavailable}
              size="sm"
            />
            {((isOutOfStock && quantity > 0) || isOverAvailableQuantity) && (
              <Badge
                variant="secondary"
                className="text-[10px] px-1.5 py-0.5 bg-orange-100 text-orange-700 hover:bg-orange-100 hover:text-orange-700"
              >
                {t("components.products.productCatalogCard.request")}
              </Badge>
            )}
          </>
        ) : isRequestable ? (
          <Button
            type="button"
            size="sm"
            onClick={handleAddProduct}
            disabled={disabled}
            variant="outline"
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4 opacity-70" />
            {t("components.products.productCatalogCard.request")}
          </Button>
        ) : isUnavailable ? (
          <div className="flex items-center gap-1 text-red-500">
            <Ban className="h-4 w-4" />
            <span className="text-xs">
              {t("components.products.productCatalogCard.unavailable")}
            </span>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={handleAddProduct}
            disabled={disabled}
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            {t("components.products.productCatalogCard.add")}
          </Button>
        )}
      </div>

      {isOverAvailableQuantity && (
        <div className={cn("text-xs text-muted-foreground", warningClassName)}>
          {t("components.products.productCatalogCard.youAreRequestingMore")}
        </div>
      )}
    </>
  );
};
